import PageHeader from '@/content/Info/Terms/PageHeader';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import {
  Typography,
  Container,
  Box,
  List,
  ListItem,
  Button
} from '@mui/material';

import useScrollTrigger from '@mui/material/useScrollTrigger';
import Fade from '@mui/material/Fade';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Fab from '@mui/material/Fab';
import * as React from 'react';
import Toolbar from '@mui/material/Toolbar';
import BackButton from '@/components/BackButton';

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
  children: React.ReactElement;
}

function ScrollTop(props: Props) {
  const { children, window } = props;
  // Note that you normally won't need to set the window ref as useScrollTrigger
  // will default to window.
  // This is only being set here because the demo is in an iframe.
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
    disableHysteresis: true,
    threshold: 100
  });
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (
      (event.target as HTMLDivElement).ownerDocument || document
    ).querySelector('#back-to-top-anchor');
    if (anchor) {
      anchor.scrollIntoView({
        block: 'center'
      });
    }
  };

  return (
    <Fade in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
      >
        {children}
      </Box>
    </Fade>
  );
}

function TermsContent(props: Props) {
  return (
    <>
      <Container maxWidth="xl">
        <PageTitleWrapper>
          <PageHeader />
        </PageTitleWrapper>
        <Toolbar id="back-to-top-anchor" />
        <ScrollTop {...props}>
          <Fab size="small" aria-label="scroll back to top">
            <KeyboardArrowUpIcon />
          </Fab>
        </ScrollTop>
        <Box
          sx={{
            p: {
              xs: 2,
              md: 3,
              lg: 4
            }
          }}
        >
          <Typography variant={'body1'}>
            Terms and Conditions These Admin Tools Terms and Conditions (these
            “Terms and Conditions”) are between Admin Tools LLC, a Delaware
            limited liability company (“Admin Tools”) and you, the Admin Tools
            user (“User”), who by accessing this Website, expressly and
            irrevocably agrees to the Terms and Conditions and to abide by the
            Terms and Conditions.
          </Typography>
          <Typography sx={{ mt: 0.1 }} variant={'body1'}>
            These Terms and Conditions are legally binding on all users of this
            Website and constitute a legally binding agreement between you
            (“User”) and Admin Tools
          </Typography>
          <Typography sx={{ mt: 0.1 }} variant={'body1'}>
            Capitalized words in these Terms and Conditions have special
            meanings given in the Section where they are first used or in
            Section 11 (Definitions). Admin Tools may modify these Terms and
            Conditions at any time, but modifications are not effective for
            Orders that are in place prior to the modification until the Order
            renews or Customer adds new Services for the monitored Web
            Application. See Section 10.20(Changes to Online Service Terms).
          </Typography>
          <Typography sx={{ mt: 0.1 }} variant={'body1'}>
            If the User of this Website does not agree with any of these Terms
            and Conditions, User may not, under any circumstances, utilize the
            Admin Tools Website.
          </Typography>
          <List
            sx={{
              listStyleType: 'decimal',
              ml: { xs: 0.1, lg: 1 },
              p: { xs: 0.1 }
            }}
          >
            <ListItem sx={{ display: 'list-item', p: { xs: 1 } }}>
              <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                Intellectual Property Rights.
              </Typography>
              <List
                sx={{
                  listStyleType: 'decimal',
                  ml: { xs: 0.2, lg: 1 },
                  p: { xs: 0.1 }
                }}
              >
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Ownership
                    <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                      All content, text, images, data, information and other
                      material displayed, available or present on this Website
                      ("Content"), including any intellectual property rights in
                      such Content (including without limitation trademarks and
                      copyrights) or other intellectual property rights
                      associated with the Website (hereinafter "Intellectual
                      Property Rights"), are the property of Admin Tools, its
                      licensors, or designated owners and are protected by
                      applicable intellectual property laws. User should assume
                      that everything User sees or reads on this Website is
                      protect by intellectual property rights, unless otherwise
                      noted, and may not be used without the written permission
                      of Admin Tools or the respective owner except as provided
                      in these Terms and Conditions.
                    </Typography>
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Authorized and Prohibited
                    <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                      Uses User may access, use, and display this Website and
                      download and print copies of the Content only for
                      non-commercial, informational, personal use, without
                      modification or alteration in any way, and only so long as
                      User complies with these Terms and Conditions and all
                      applicable laws. If User violates these Terms and
                      Conditions and/or any applicable laws, your permission to
                      use the Website terminates. User may not otherwise
                      reproduce, sell, publish, distribute, modify, display, or
                      use any Content or portion of this Website without the
                      prior written permission of Admin Tools; provided,
                      however, if the functionality of the Website permits the
                      e- mailing of certain Content or a link through the use of
                      an "e-mail to a friend" (or similar) icon, User may send
                      that particular Content to others by e-mail. User will not
                      infringe upon any Intellectual Property Rights or remove
                      or modify related intellectual property and/or proprietary
                      notices contained in this Website or the Content.
                    </Typography>
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Registration and Passwords
                    <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                      Admin Tools may or may not at times require a User to have
                      a password and provide registration details to access this
                      Website or portions of this Website. If Admin Tools does
                      require a password and registration details, it shall be a
                      condition of use of this Website that all the details User
                      provides are correct, current, and complete. If Admin
                      Tools believes that the details are not correct, current,
                      or complete, Admin Tools will have the right to refuse
                      User access to the Website, or any of its resources, and
                      to suspend and/or terminate User's account. User is
                      responsible for maintaining the confidentiality of any
                      password(s) and registration details User is given to
                      access this Website, and User is fully responsible for all
                      activities that occur under User's password(s) and/or
                      registration details. User agrees to notify Admin Tools
                      immediately of any unauthorized use of User’s password(s)
                      and/or registration details. Admin Tools reserves the
                      absolute right, in its sole discretion, not to issue a
                      password to any person or entity.
                    </Typography>
                  </Typography>
                </ListItem>
              </List>
            </ListItem>
            <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
              <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                Services
              </Typography>
              <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                Here at Admin Tools, we take your privacy seriously and will
                only collect and/or use the information below in accordance with
                this Privacy Policy (“Policy”). By visiting our website and/or
                enrolling in our services, you agree to be bound by the terms of
                this Policy.
              </Typography>
              <List
                sx={{
                  listStyleType: 'decimal',
                  ml: { xs: 0.1, lg: 1 },
                  p: { xs: 0.1 }
                }}
              >
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Services
                    <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                      The Services will include those features listed in the
                      Customer’s Order and related graphical user interfaces and
                      application programming interfaces
                    </Typography>
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Warranties
                    <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                      Admin Tools warrants that (i) the Services will conform in
                      all material respects with the description of the Services
                      stated in the Order and with its general release user
                      Documentation, and (ii) if Customer has configured the
                      Services in accordance with the Documentation for a “best
                      case build” or “common practice build” the data returned
                      by the Services will be materially accurate (the “Services
                      Warranty”). Admin Tools warrants that it will provide the
                      Services in compliance with the laws applicable to the
                      provision of the Services.
                    </Typography>
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Support
                    <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                      Admin Tools will publish support Documentation online on
                      its Site. Admin Tools will provide live technical
                      assistance via email, ticket, telephone and chat during
                      its standard support hours. Admin Tools standard support
                      hours are published on its Site and may vary, excluding
                      federal public holidays in the United States and Admin
                      Tools announced support holidays. Admin Tools offers
                      urgent issue support 24x7, but the Customer must designate
                      an off-hours support request as “emergency” or “urgent” to
                      receive a response during off-hours.
                    </Typography>
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Evaluation
                    <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                      Use of the Services. Admin Tools may provide Customer with
                      access to its Services for Customer’s use in evaluating
                      the Services for a potential purchase. Notwithstanding
                      anything in the contrary in the Agreement, Services
                      provided on an evaluation basis, whether provided at no
                      charge or for a fee, are provided “as is”, without any
                      service commitments, warranties or representations
                      whatsoever. Admin Tools has no obligation to provide
                      support for any evaluation services. Any support that is
                      provided is provided on an “as is” and “as available”
                      basis. Admin Tools has no liability for any harm or damage
                      arising out of or in connection with the Customer’s use of
                      the services during any evaluation period.
                    </Typography>
                  </Typography>
                </ListItem>
              </List>
            </ListItem>
            <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
              <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                Customer Information
              </Typography>
              <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                <Typography
                  sx={{ mt: 2, mr: 0.2, fontWeight: 'bold' }}
                  component="span"
                  variant={'body1'}
                >
                  “Customer Information”
                </Typography>
                is any data or information that Customer submits to Admin Tools
                in connection with the Admin Tools Services account or that is
                created or accessed by Customer or Admin Tools by means of the
                Admin Tools Services. Customer Information consists solely of:
                (i) information submitted to Admin Tools to establish a Services
                account, receive support, and use the Services, such as business
                contact information of Customer’s personnel, payment
                information, and content of support requests, and (ii) data or
                information collected by means of the Services. Unless Customer
                elects to install Admin Tools code snippet on the Customer’s Web
                server, the only information collected by Admin Tools by means
                of the Services is information that is generally available to
                any public user of the Customer’s monitored Web Application(s).
                If Customer elects to install Admin Tools code snippet on its
                Web server, then the Admin Tools Service may also capture
                information that is captured by Web servers generally, such as
                visitor IP address, date and time of visit, and duration of
                visit. Customer Information is Customer’s Confidential
                Information covered by Section 10.2 (Confidential Information).
                As between Customer and Admin Tools, Customer owns and retains
                ownership and all rights in Customer Information and Admin Tools
                may use Customer Information only for the purpose of providing
                the Services, communicating with Customer and administering the
                Customer’s account, exercising its legal rights and remedies in
                connection with the Agreement, and as otherwise expressly
                permitted by Section 10.2 (Confidential Information). Admin
                Tools will destroy Customer Information on Customer’s request
                following termination. “Customer Information” does not include
                system generated data about Customer’s use of the Services, such
                as user navigation on the Services interface and resource
                utilization. Admin Tools may use system generated data for
                insights to help it improve general support and service.
                Customer authorizes Admin Tools to de-identify and aggregate
                Customer Information with similar de-identified data of other
                customers and with anonymous data from public or private data
                sets and analyze the resulting data sets for insights to help it
                improve its services generally, provided that Admin Tools method
                of de-identification must make it technically infeasible for any
                data to be re-identified to any of Customer’s customers or to
                Customer.
              </Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
              <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                Security and privacy
              </Typography>
              <List sx={{ listStyleType: 'decimal', ml: { xs: 0.1, lg: 1 } }}>
                <ListItem sx={{ display: 'list-item' }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Security
                  </Typography>
                  <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                    On Customer’s request, Admin Tools will provide a
                    description of the security measures it uses to protect
                    Customer Information (the “Security Materials”). Admin Tools
                    will protect Customer Information using measures at least as
                    stringent as those stated in the Security Materials for the
                    Term and following the Term for so long as Admin Tools
                    retains any Customer Information. Customer acknowledges that
                    Admin Tools cannot guarantee complete security and that
                    there is always a security risk involved in the use of an
                    online service. Customer acknowledges that the measures
                    described in the Security materials are reasonable and
                    appropriate given the nature of the Customer Information and
                    agrees that Admin Tools is not responsible to Customer for
                    any harm Customer suffers as a result of a security breach
                    unless the breach resulted from Admin Tools failure to
                    maintain the security measures described in this Security
                    Materials. Customer acknowledges that the Security Materials
                    are Admin Tools sensitive Confidential Information covered
                    by the restrictions on use and disclosure stated in Section
                    10.2 (Confidential Information).
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item' }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Privacy
                  </Typography>
                  <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                    Customer Information includes Personal Data as follows: (i)
                    business contact information and Admin Tools log in
                    credentials of Customer’s personnel and their interactions
                    with the Admin Tools application or support portal; (ii)
                    information volunteered by Customer’s personnel as part of a
                    support request or other communication, and (iii) if
                    Customer elects to install the Admin Tools code snippet on
                    its Web server, the IP address of visitors to the monitored
                    Web Application and other data about their visit that is
                    captured by Web servers generally, such as date and time of
                    visit and duration of visit. Admin Tools makes the following
                    commitments with respect to Customer’s Personal Data: (i)
                    Admin Tools will provide Customer with access to the
                    Personal Data as necessary for Customer to comply with
                    applicable legal requirements for access, destruction, or
                    modification of the Personal Data at the request of a data
                    subject; and (ii) Admin Tools will notify Customer in
                    writing without undue delay if it discovers that any
                    Personal Data has been accessed, used, disclosed, destroyed,
                    or corrupted other than as permitted by the Agreement, will
                    provide all information reasonably available to it regarding
                    the reported event, will cooperate with Customer’s
                    reasonable requests to mitigate and remediate the event.
                    Customer acknowledges and agrees that Admin Tools affiliates
                    and services suppliers may be based outside of the
                    geographic jurisdiction in which Customer’s data subjects
                    are located. If legally required the parties will enter into
                    good faith negotiations for such agreements as are necessary
                    for the transfer of the personal data across national
                    borders.
                  </Typography>
                </ListItem>
              </List>
            </ListItem>
            <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
              <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                Integration Feature
              </Typography>
              <Typography sx={{ mt: 0.2 }} variant={'body1'}>
                Customer Information includes Personal Data as follows: (i)
                business contact information and Admin Tools log in credentials
                of Customer’s personnel and their interactions with the Admin
                Tools application or support portal; (ii) information
                volunteered by Customer’s personnel as part of a support request
                or other communication, and (iii) if Customer elects to install
                the Admin Tools code snippet on its Web server, the IP address
                of visitors to the monitored Web Application and other data
                about their visit that is captured by Web servers generally,
                such as date and time of visit and duration of visit. Admin
                Tools makes the following commitments with respect to Customer’s
                Personal Data: (i) Admin Tools will provide Customer with access
                to the Personal Data as necessary for Customer to comply with
                applicable legal requirements for access, destruction, or
                modification of the Personal Data at the request of a data
                subject; and (ii) Admin Tools will notify Customer in writing
                without undue delay if it discovers that any Personal Data has
                been accessed, used, disclosed, destroyed, or corrupted other
                than as permitted by the Agreement, will provide all information
                reasonably available to it regarding the reported event, will
                cooperate with Customer’s reasonable requests to mitigate and
                remediate the event. Customer acknowledges and agrees that Admin
                Tools affiliates and services suppliers may be based outside of
                the geographic jurisdiction in which Customer’s data subjects
                are located. If legally required the parties will enter into
                good faith negotiations for such agreements as are necessary for
                the transfer of the personal data across national borders.
              </Typography>
            </ListItem>
            <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
              <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                Fees and payments
              </Typography>
              <List
                sx={{
                  listStyleType: 'decimal',
                  ml: { xs: 0.2, lg: 1 },
                  p: { xs: 0.1 }
                }}
              >
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Payments
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    Unless Customer has made other arrangements with Admin
                    Tools, Customer must authorize and maintain a current valid
                    means for Admin Tools to collect its fees for the Services
                    via payment card at all times during the Term.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Late Payments
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    Admin Tools may suspend or terminate Customer’s Services or
                    the Agreement if Customer’s payment is overdue, including if
                    Admin Tools charge to Customer’s payment card or account is
                    rejected. If Admin Tools suspends Customer’s account for
                    late payment, Customer must pay Admin Tools reasonable
                    reinstatement fee.
                  </Typography>
                </ListItem>
              </List>
            </ListItem>
            <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
              <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                Customer obligations, restrictions on use
              </Typography>
              <List
                sx={{
                  listStyleType: 'decimal',
                  ml: { xs: 0.2, lg: 1 },
                  p: { xs: 0.1 }
                }}
              >
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Web Application
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    Customer will use the Services only to monitor the Web
                    Application(s) identified in the Order.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Unsuitable Conduct
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    User will not engage in conduct on or in connection with
                    this Website that is illegal, misleading, infringing,
                    defamatory, obscene, offensive, or otherwise objectionable.
                    User will not cause damage, embarrassment, or adverse
                    publicity to Admin Tools.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    User Cooperation and Notification
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    User will cooperate with all reasonable requests of Admin
                    Tools and will notify Admin Tools promptly upon learning of
                    any actual or suspected breach of these Terms and Conditions
                    by User or unauthorized use or abuse of this Website.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Fees
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    Customer will pay fees when due. Customer will not use or
                    attempt to use the Services in a way that undermines Admin
                    Tools ability to correctly calculate its fees.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    No Service Provider Use or Resale
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    Customer may use the Services only in connection with its
                    and its affiliates internal business purposes. Customer may
                    not resell the Services or use the Services to provide
                    services to others.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Customer’s Security Obligations
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    Customer will use reasonable security precautions in
                    connection with its use of the Services. For example,
                    Customer will use commercially reasonable efforts to protect
                    its systems and data from malware, will require its users to
                    establish reasonably secure passwords that are different
                    than the passwords used for other online services, and will
                    conduct anti-phishing training.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Authorized Users
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    Customer will authorize as Services users only the
                    following: (i) its personnel, (ii) the personnel of
                    Customer’s affiliates, and (iii) the personnel of Customer’s
                    and its affiliates’ contractors for their use in supporting
                    Customer’s or the affiliates’ internal business operations.
                    Customer is solely responsible for deactivating or updating
                    permissions and authentication credentials for Customer’s
                    users, such as on the termination of employment of a user.
                    Customer authorizes Admin Tools to act on the instructions
                    of a user who authenticates using active account
                    credentials.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Compliance with Law, Acceptable Use
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    Customer will use the Services in compliance with applicable
                    law and only to monitor Web applications that it operates in
                    compliance with applicable law and industry acceptable use
                    norms as described in the acceptable use policies of tier
                    one public cloud infrastructure providers such as Amazon Web
                    Services, Azure, and Google.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Indemnification
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    Each User will defend, indemnify, and hold harmless Admin
                    Tools and their respective officers, directors, employees,
                    and agents, and any of its licensors, website partners, or
                    contractors from any demands, claims, damages, liabilities,
                    expenses or harms, including attorney’s fees, arising in
                    connection with User’s actions or omissions, including use
                    of this Website, online conduct, breach of these Terms and
                    Conditions, or dealings or transactions with other persons
                    resulting from use of this Website.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Representations and Warranties
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    Customer represents and warrants to Admin Tools as of the
                    effective date of each Order and on an ongoing basis that:
                    (i) the information Customer submits about itself and
                    Customer’s activities to establish a Services account with
                    Admin Tools and place an Order is true, correct, and
                    complete, (ii) Customer has not been the target of any legal
                    or regulatory investigations or proceedings in connection
                    with Customer’s business activities, (iii) Customer has all
                    necessary rights and authority to authorize Admin Tools to
                    use and process Customer Information as permitted by the
                    Agreement, and (iv) Customer has and has made disclosures
                    and obtained consents as required for Admin Tools processing
                    of Personal Data as permitted by the Agreement.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'h4'}>
                    Third Party Sites and Other Information
                  </Typography>
                  <Typography sx={{ mt: { xs: 1, lg: 3 } }} variant={'body1'}>
                    As a convenience to User, this Website may contain Content,
                    links, and other information submitted by third parties over
                    whom Admin Tools has no control or responsibility. Admin
                    Tools has no obligation to monitor, control, or restrict the
                    use of this Website or third party websites accessible via
                    links on this Website. These other sites are not under the
                    control of Admin Tools, and User acknowledges that (whether
                    or not such sites are affiliated in any way with Admin
                    Tools) Admin Tools is not responsible for the accuracy,
                    copyright compliance, legality, decency, or any other aspect
                    of the content of such sites. The inclusion of such a link
                    does not imply endorsement of any site by Admin Tools or any
                    association with its operators. Moreover, Admin Tools has no
                    obligation to verify any Content submitted by any user or
                    third party or to provide any registration or other
                    information to User concerning any Content.
                  </Typography>
                </ListItem>
              </List>
            </ListItem>
            <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
              <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                Term, Termination, Suspension
              </Typography>
              <List
                sx={{
                  listStyleType: 'decimal',
                  ml: { xs: 0.1, lg: 1 },
                  p: { xs: 0.1 }
                }}
              >
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Term
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    The initial term of each Order begins on Order submission
                    and continues for the number of months stated in the Order,
                    or for one (1) month if the Order does not state a specific
                    number of months. Customer may renew an Order by paying
                    Admin Tools renewal fees.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Termination
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Either party may terminate an Order or any or all Orders
                    between the parties, on written notice if the other party is
                    in material violation of any term of an Order or these
                    Service Terms, provided that if the violation is curable the
                    terminating party must first give the other party a written
                    notice describing the violation in reasonable detail and at
                    least ten (10) days to cure the violation.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Suspension
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Admin Tools may suspend Customer’s access to the Services
                    and Customer Information during any period that Customer is
                    in material breach of the Agreement or Customer’s access to
                    the Services or Customer Information creates a material
                    security vulnerability. Admin Tools will give Customer at
                    least two (2) business days’ advance notice of the
                    suspension unless the suspension is made under emergency
                    circumstances. Admin Tools will reinstate Customer’s access
                    to the Services when the grounds for suspension are cured
                    unless Admin Tools has already terminated the Agreement as
                    described in this Section.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Surviving Provisions
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    The following sections survive the expiration or earlier
                    termination of the Agreement: Section 3 (Customer
                    Information); Section 4 (Security and Privacy) for so long
                    as Admin Tools retains any Customer Information, Section 6
                    (Fees and Payment), Section 8 (Term, Termination and
                    Suspension), Section 9 (Remedies, Disclaimers,
                    Indemnification, Limitations), Section 10 (General Terms),
                    and Section 11 (Definitions) and any other terms that by
                    their nature are intended to survive expiration or
                    termination.
                  </Typography>
                </ListItem>
              </List>
            </ListItem>
            <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
              <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                Remedies, Disclaimers, Indemnification, Limitations
              </Typography>
              <List
                sx={{
                  listStyleType: 'decimal',
                  ml: { xs: 0.1, lg: 1 },
                  p: { xs: 0.1 }
                }}
              >
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Warranty Remedy
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    If Admin Tools fails to meet the Service Warranty, Admin
                    Tools will attempt to cure the failure or if it is unable to
                    cure the failure through commercially reasonable efforts
                    will refund the fees paid for the month during which the
                    failure occurred. However, to be eligible for a warranty
                    remedy under this section, Customer must give a written
                    notice describing the failure no later than ten (10) days
                    following the end of the month and cooperate with Admin
                    Tools reasonable efforts to cure the failure. The remedies
                    stated in this Section are Customer’s sole and exclusive
                    remedy for Admin Tools breach of the Services Warranty.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    No Other Warranty
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Except as expressly stated in these Services Terms or an
                    SLA, the Services are provided “as is”. Admin Tools
                    disclaims any implied warranties, such as a warranty of
                    merchantability, fitness for a particular purpose, and
                    non-infringement, and any representation or warranty that
                    may arise through a course of dealing. Customer acknowledges
                    that the use of the Service may not be uninterrupted, error
                    free, or completely secure. Customer represents that
                    Customer has not relied on any representation or warranty
                    other than those stated in these Services Terms.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Indemnification
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Admin Tools shall defend Customer and its affiliates, and
                    each of their respective employees, directors, members,
                    managers, officers, owners, and agents (collectively the
                    “Customer Indemnitees”) from any third- party claim
                    asserting that Customer’s use of the Services as permitted
                    by the Agreement infringes or misappropriates the copyright,
                    patent, trade secret or other intellectual property right of
                    the third party recognized in the United States (either
                    under direct United States law or pursuant to an
                    international treaty to which the United States is a party)
                    (an “IP Claim”) and pay any resulting liability, judgment,
                    loss, damage, cost and other expense (including reasonable
                    attorneys’ fees) (“Losses”) awarded to the third party by a
                    court of competent jurisdiction, or in the alternative shall
                    settle the IP Claim at Admin Tools expense, provided that
                    Customer’s consent is required if the settlement does not
                    fully resolve the IP Claim or requires Admin Tools or any
                    Customer Indemnitee to make a statement admitting
                    culpability. Notwithstanding the foregoing, Admin Tools
                    shall not have any obligation to indemnify an IP Claim to
                    the extent it results from (i) an unauthorized modification
                    of the Services by any of the Customer Indemnitees or
                    persons acting on their instruction, or (ii) an unauthorized
                    combination or use of the Services by any of the Customer
                    Indemnitees or persons acting on their instruction with any
                    software, hardware or other technology, information or
                    materials not provided by Admin Tools unless the combination
                    or use is reasonably contemplated by the nature of the
                    Services or their authorized use. If an IP Claim is
                    asserted, or in Admin Tools reasonable opinion is reasonably
                    probable of assertion, Admin Tools may terminate the
                    Agreement without liability except to refund prepaid fees
                    for unused Services, provided that Admin Tools must first
                    have used commercially reasonable efforts to resolve the IP
                    Claim or modify the Services to avoid the IP Claim without
                    material diminishment of functionality. This Section states
                    Customer’s sole and exclusive remedies and Admin Tools sole
                    and exclusive obligations with respect to claims of
                    intellectual property infringement.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Limitation if Liability
                  </Typography>
                  <List
                    sx={{
                      listStyleType: 'decimal',
                      ml: { xs: 0.1, lg: 1 },
                      p: { xs: 0.1 }
                    }}
                  >
                    <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                      <Typography
                        sx={{ mt: { xs: 0.2, lg: 3 } }}
                        variant={'h4'}
                      >
                        No Consequential, Indirect Damages
                      </Typography>
                      <Typography
                        sx={{ mt: { xs: 0.2, lg: 3 } }}
                        variant={'body1'}
                      >
                        Except for claims arising from a party’s breach of
                        Section 10.2 (Confidential Information), or claims based
                        on the party’s intentional breach of the other party’s
                        intellectual property rights, neither party nor its
                        affiliates, licensors, suppliers, resellers,
                        distributors or subcontractors is liable to the other
                        for any lost profits, lost revenue, lost business
                        opportunity, or any indirect, special, incidental,
                        punitive, or consequential loss or damage of any kind
                        arising in connection with this Agreement, or any loss
                        or damage that could have been avoided by the claiming
                        party’s reasonable mitigation, even if the party has
                        been advised of or should be aware of the possibility of
                        such damages. For avoidance of doubt, damages of the
                        type described in this subsection that are awarded by a
                        court or other tribunal to a third party and are covered
                        under Section
                      </Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                      <Typography
                        sx={{ mt: { xs: 0.2, lg: 3 } }}
                        variant={'h4'}
                      >
                        Maximum Liability
                      </Typography>
                      <Typography
                        sx={{ mt: { xs: 0.2, lg: 3 } }}
                        variant={'body1'}
                      >
                        Notwithstanding anything to the contrary in this
                        Agreement, excluding: (i) claims arising from a party’s
                        gross negligence, recklessness, or intentional tort,
                        (ii) claims arising from a party’s breach of Section
                        10.2 (Confidential Information), (iii) claims based on
                        the party’s intentional infringement or misappropriation
                        of the other party’s intellectual property rights, and
                        (iv) payment obligations under Section 6 (Fees and
                        Payments), the maximum aggregate liability of a party
                        and its affiliates, licensors, suppliers, resellers,
                        distributors, and subcontractors under or in connection
                        with this Agreement for any type of damages, claims or
                        obligations shall not exceed the amount of fees paid or
                        payable by Customer under the Order giving rise to the
                        claim for the 12 months preceding the event giving rise
                        to the claim. For clarity, the maximum aggregate
                        monetary limit stated in this subsection is not “per
                        incident” but is an aggregate limitation applicable to
                        all claims arising under or regarding this Agreement.
                      </Typography>
                    </ListItem>
                    <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                      <Typography
                        sx={{ mt: { xs: 0.2, lg: 3 } }}
                        variant={'h4'}
                      >
                        General
                      </Typography>
                      <Typography
                        sx={{ mt: { xs: 0.2, lg: 3 } }}
                        variant={'body1'}
                      >
                        Customer acknowledges that Admin Tools has set its fees
                        and entered into the Order in reliance on the
                        limitations of remedies and liability stated in these
                        Terms, and that these limitations reflect an agreed
                        allocation of risk between Customer and Admin Tools. The
                        limitations stated in this Section shall apply to any
                        liability arising from any cause of action whatsoever,
                        whether in contract, tort, commercial code, strict
                        liability or otherwise, even if a limited remedy fails
                        of its essential purpose. If these limitations as
                        written are not permitted by applicable law, they shall
                        apply to the extent permitted by applicable law.
                      </Typography>
                    </ListItem>
                  </List>
                </ListItem>
              </List>
            </ListItem>
            <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
              <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                General terms
              </Typography>
              <List sx={{ listStyleType: 'decimal', ml: { xs: 0.1, lg: 1 } }}>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Feedback
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Customer hereby licenses to Admin Tools any feedback or
                    suggestions that Customer may provide regarding the Services
                    or Admin Tools other existing or proposed products or
                    services on a perpetual, irrevocable, royalty free,
                    worldwide, unconditional, fully sublicensable and
                    transferable basis, including the right to make, have made,
                    use, sell, offer to sell, import, copy, display, perform,
                    modify, distribute in modified or unmodified form, and
                    commercialize any intellectual property, without accounting
                    to Customer for revenues or profits.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Confidential Information
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Neither party may use the other party's Confidential
                    Information except in connection with the performance of its
                    obligations under this Agreement or the permitted use of the
                    Services, as applicable, the exercise of the party’s legal
                    rights under this Agreement, or as may be otherwise
                    permitted under this Agreement or required by law. Each
                    party agrees not to disclose the other party’s Confidential
                    Information to any third person except as follows: (i) to
                    the party’s respective service providers, agents and
                    representatives, provided that such service providers,
                    agents or representatives are bound by written
                    confidentiality measures that are at least as stringent as
                    these terms; (ii) in response to a subpoena or other
                    compulsory legal process, provided that each party agrees to
                    give the other advance written notice of at least seven (7)
                    days prior to disclosing the other party’s Confidential
                    Information under this subparagraph (or prompt notice in
                    advance of disclosure, if seven (7) days advance notice is
                    not reasonably feasible), unless the law or a reasonable
                    interpretation of it, forbids such notice, or (iii) as
                    otherwise expressly permitted by the Agreement. On
                    expiration or earlier termination of the Agreement, each
                    party will return or destroy the other party’s Confidential
                    Information. If a party believes it is not feasible to
                    completely destroy the other party’s Confidential
                    Information as required by this Section, it will give the
                    other party written notice describing the Confidential
                    Information and the circumstances that make destruction
                    infeasible. All Confidential Information retained by each
                    party following termination of the Agreement remains subject
                    to the requirements of this Section. Each party will use
                    commercially reasonable care to safeguard the other’s
                    Confidential Information. Admin Tools obligations to use
                    reasonable care safeguard Customer Information is met by its
                    use of the measures described in its Security Materials.
                    Each of Customer and Admin Tools is responsible for a breach
                    of this Section by its service providers, agents and
                    representatives to whom it has disclosed the other party’s
                    Confidential Information.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Governing Law, Venue, Waiver of Jury Trial
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    The Order and these Services Terms are governed by and
                    should be interpreted under the laws of the State of
                    Delaware and the United States of America, as applicable,
                    without giving effect to any conflicts of law principles
                    that would require the application of the law of a different
                    jurisdiction. The parties expressly and irrevocably disclaim
                    and waive the application of the United Nations Convention
                    on Contracts for the International Sale of Good and the
                    Uniform Computer Information Act. The parties agree that
                    neither the Services nor the Admin Tools Technology are
                    “goods” covered by any State version of the Uniform
                    Commercial Code. Each party agrees that it shall bring any
                    lawsuit or other legal action related to this Agreement in
                    the State or Federal courts sitting in Delaware. Neither
                    party shall dispute the personal jurisdiction of such
                    courts, and each party waives any objection it may have as
                    to the venue of such court. To the extent permitted by
                    applicable law, each party waives the right to a trial by
                    jury in respect of any litigation arising out of this
                    Agreement or its subject matter. A party may not bring an
                    action in relation to this Agreement more than two (2) years
                    after the date that the cause of action accrued.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Notices
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Customer’s notices must be submitted via ticket on the
                    customer portal, and copied by electronic mail to
                    support@admintools.io. Admin Tools notices to Customer must
                    be given to Customer’s primary account contact at the email
                    address in the Admin Tools portal. If Customer sends a
                    notice of breach of the Agreement, a legal dispute, a legal
                    claim, or other legal matter, Customer must copy Customer’s
                    notice to support@admintools.io, and must also send
                    Customer’s notice via first class United States mail to
                    Admin Tools physical address appearing on its Site on the
                    day the notice is transmitted electronically. Admin Tools
                    legal notices to Customer must be copied via first class
                    United States mail to Customer’s physical notice address, or
                    if Customer’s physical address is outside of the United
                    States, via a reputable and established international
                    priority mail service on the date the notice is transmitted
                    electronically. Notices are deemed given, received and
                    effective as of the time transmitted by electronic mail, or
                    if that time does not fall on a business day, as of the
                    beginning of the first business day following the time
                    transmitted. Notices must be given in the English language.
                    A party may change its address for notice by giving notice
                    in the manner stated in this Section.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Informal Dispute Resolution
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Except for a request for injunctive relief as permitted by
                    Section 10.6 (Injunctive Relief), each party agrees that it
                    will not file a lawsuit or other legal action in connection
                    with the Services, any Order, or these Services Terms,
                    unless it has first given the other party written notice of
                    the dispute and attempted to resolve the dispute through
                    good faith negotiation.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Injunctive Relief
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Notwithstanding anything in this Agreement to the contrary,
                    this Agreement does not prohibit, condition or delay a
                    party’s right to seek injunctive relief in any court of
                    competent jurisdiction to address the other party’s
                    infringement or misappropriation of its intellectual
                    property, or the other party’s breach of its confidentiality
                    obligations under this Agreement.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Export Compliance
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    The Services may be subject to export laws and regulations
                    of the United States and other jurisdictions. Customer may
                    not permit users to access or use the Services in a
                    U.S.-embargoed country or in violation of any U.S. export
                    law or regulation, or in a manner that causes Admin Tools to
                    be in violation of U.S. export laws, even if the use is
                    permitted by the laws applicable to Customer or Customer’s
                    user or customer. Each party represents that it is not on
                    any restricted persons list maintained by the U.S., Canada,
                    or any member of the European Union.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Anti-Corruption
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Each party represents that it has not received or been
                    offered any illegal or improper bribe, kickback, payment,
                    gift, or thing of value from the other’s employees or agents
                    in connection with this Agreement. Reasonable gifts and
                    entertainment provided in the ordinary course of business do
                    not violate the above restriction. If either party learns of
                    any violation of the above restriction, it will promptly
                    notify the other party.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Force Majeure
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Except for Customer’s payment obligations, neither party is
                    in violation of the Agreement if the failure to perform is
                    due to an event beyond that party’s reasonable control, such
                    as a significant failure of the power grid or Internet,
                    denial of service attacks, natural disaster, war, riot,
                    insurrection, epidemic, strikes or other organized labor
                    action, terrorism, or other acts or events for which
                    precautions are not generally taken in the industry.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Assignment
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Neither party may assign any of its rights or obligations
                    hereunder, whether by operation of law or otherwise, without
                    the other party’s prior written consent (not to be
                    unreasonably withheld); provided, however, either party may
                    assign this Agreement in its entirety (including all
                    Orders), without the other party’s consent to its affiliate
                    or in connection with a merger, acquisition, corporate
                    reorganization, or sale of all or substantially all of its
                    assets. Subject to the foregoing, this Agreement will bind
                    and inure to the benefit of the parties, their respective
                    successors and permitted assigns.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Assignment
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Each party will consider the other’s requests to participate
                    in the development of product whitepapers, testimonials for
                    Web publication, and other like materials for publication,
                    provided that neither party may issue any press release or
                    other publicity regarding this Agreement or the relationship
                    or transactions contemplated by this Agreement without the
                    prior review and consent of the other party. Customer agrees
                    that Admin Tools may identify Customer as its customer on
                    its sales presentations and its website where it identifies
                    its customers generally, and Admin Tools agrees that
                    Customer may identify it as Customer’s service provider on
                    Customer’s website. Each party may use the other party’s
                    trade or service marks in connection with authorized use of
                    its name and other approved activities. Each party shall use
                    the other’s trademarks subject to the other party’s
                    reasonable trademark usage guidelines that are communicated
                    to the party from time to time.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Relationship of the Parties
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    The parties are independent contractors. Neither party is
                    the agent of the other, and neither party is authorized to
                    make any representations, contract, or commitment on behalf
                    of the other. The use of the words “partner” or
                    “partnership” in this Agreement or otherwise refers only to
                    a business relationship, and does not create or reflect any
                    legal partnership, joint venture, or other fiduciary or
                    other special relationship between the persons described as
                    partners. The parties do not agree to any exclusivity in
                    regards to the subject matter of this Agreement and each
                    party is free to contract with third parties, including
                    competitors of the other party, for transactions of the type
                    covered by this Agreement in any market, worldwide.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Relationship of the Parties
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    In calculating any period of time under this Agreement, the
                    day of the act, event or default from which the designated
                    period of time begins to run is not to be included. The term
                    “person” refers to any legal person, and may mean a natural
                    person (individual), a legally created person (such as an
                    entity, trustee, or executor), or an entity (such as a
                    corporation, partnership, or limited liability company). The
                    word “personnel” refers to a person’s employees and
                    individual contractors who are under the person’s direct
                    supervision. The word “affiliate” refers to an individual or
                    entity that controls, is controlled by, or is under common
                    control with the person referred to, where control means
                    ownership of the majority of voting interests of an entity
                    or the right to control the policies of the entity by means
                    of a controlling number of seats on the entity’s governing
                    body. The use of the word “including” should be read to mean
                    “including, without limitation.” All references to monetary
                    amounts mean United States Dollars. The term “parties,”
                    either in lower- or upper-case form, refers to the
                    signatories to this Agreement unless expressly stated as a
                    “third party.” For notice purposes, the words “business
                    day,” “business hours,” or the like means Monday – Friday,
                    7:00 a.m. – 7:00 p.m., United States Eastern time, excluding
                    federal public holidays in the United States. A reference to
                    “day” shall mean a calendar day, unless expressly designated
                    as a “business” day. All software and other technology
                    provided for Customer’s use is licensed and not sold; any
                    references to a sale or purchase of software or other
                    technology means the sale or purchase of a subscription
                    service. Any requirement in this Agreement that a statement
                    be written, in writing, or a like requirement is satisfied
                    by an email or other digital form of writing unless
                    expressly stated otherwise. Nouns stated in the singular
                    imply the plural as indicated by the context, and pronouns
                    that are gender specific refer to either gender. The Section
                    captions in this Agreement are for convenience only; they
                    are not part of this Agreement and may not be used to
                    interpret the terms of this Agreement.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Third-Party Beneficiaries
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    There are no other third-party beneficiaries under this
                    Agreement.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Severability
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    In the event one or more of the terms of this Agreement are
                    adjudicated invalid, illegal, or unenforceable, the
                    adjudicating body may either interpret this Agreement as if
                    such terms had not been included, or may reform such terms
                    to the limited extent necessary to make them valid, legal or
                    enforceable, consistent with the economic and legal
                    incentives underlying the Agreement.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Changes to the Services
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Customer agrees that Customer’s purchases are not contingent
                    on the delivery of any future functionality or features, or
                    dependent on any oral or written public comments made by
                    Admin Tools regarding future functionality or features.
                    Customer acknowledges that Admin Tools may modify or suspend
                    any of its Services offerings at any time. If a modification
                    or suspension materially and adversely affects Customer’s
                    use of the Services, Customer may terminate the Agreement by
                    giving written notice of the change no later than (30) days
                    following the date of the change and receive a refund of any
                    prepaid fees as Customer’s sole and exclusive remedy
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    General Representations
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Each party represents that it has validly entered into this
                    Agreement and has the legal power to do so.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Representation by Individual Submitting the Order
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    The individual submitting the Order represents that: (i) if
                    the individual submits the Order on his or her own behalf
                    (including as a sole proprietor), the individual is old
                    enough to enter into contracts and otherwise has the legal
                    capacity to enter into contracts under applicable law; or
                    (ii) if the individual submits the Order on behalf of a
                    company or other legal entity, the individual has the legal
                    power and authority to bind that entity to the Order and
                    these Service Terms.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Changes to Online Service Terms
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    Admin Tools may amend these Services Terms at any time in
                    its sole discretion. Any amendment will become effective as
                    to Customer’s Order on the first renewal of the Order that
                    follows the publication of the amendment by at least thirty
                    (30) days. If the amendment materially and adversely impacts
                    Customer’s use of the Services, Customer may terminate the
                    Agreement by giving written notice no later than thirty (30)
                    days following the date the amendment is published and
                    receive a refund of prepaid fees for unused Services as
                    Customer’s sole and exclusive remedy.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Entire Agreement and Order of Precedence
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    This Agreement is the entire agreement between Customer and
                    Admin Tools regarding Customer’s use of Services and
                    supersedes all prior and contemporaneous agreements,
                    proposals or representations, written or oral, concerning
                    its subject matter. No modification, amendment, or waiver of
                    any provision of these Service Terms or an Order will be
                    effective unless in writing and signed by the party against
                    whom the modification, amendment or waiver is to be
                    asserted. The parties agree that any preprinted term or
                    condition stated in any business form is void. In the event
                    of any conflict or inconsistency among the following
                    documents, the order of precedence shall be: (1) the
                    applicable Order, (2) these Services Terms, and (3) the
                    Documentation. Customer’s use of the Services, both during
                    any free use period and Customer’s Order Term, is subject to
                    all of the terms, conditions, and restrictions stated in
                    Customer’s Order and these Service Terms, and any
                    restrictions stated on a page on the Site that is part of
                    Customer’s Order.
                  </Typography>
                </ListItem>
              </List>
            </ListItem>
            <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
              <Typography sx={{ mt: { xs: 0.1, lg: 3 } }} variant={'h4'}>
                Definitions
              </Typography>
              <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                These terms, when capitalized, have the meanings stated:
              </Typography>
              <List
                sx={{
                  listStyleType: 'decimal',
                  ml: { xs: 0.1, lg: 1 },
                  p: { xs: 0.1 }
                }}
              >
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Agreement
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means these Service Terms and the Order, collectively
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Confidential Information
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means any non-public information that a party (the
                    “recipient”) receives from or learns about the other party
                    (the “discloser”) or the discloser’s licensors or suppliers
                    as a result of activities contemplated by the Agreement that
                    the recipient should reasonably understand to be
                    confidential, given the nature of the information or the
                    circumstances of its disclosure, but not including any
                    information that is developed by the recipient independently
                    and without reference to the discloser’s Confidential
                    Information, or information that is or becomes available to
                    recipient from a source other than the discloser so long as
                    the source did not, to recipient’s knowledge, acquire the
                    information as a result of a violation of a duty of
                    confidentiality to the discloser. Customer’s Confidential
                    Information includes Customer Information. Admin Tools
                    Confidential Information includes pricing and service terms,
                    product roadmap information, security information, and
                    non-public technology.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Customer Information
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    has the meaning given in Section 2 (Customer Information).
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Documentation
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means Admin Tools general release user and administration
                    guides, FAQs, Whitepapers and other materials designed to
                    explain the use of the Services, but not including any
                    marketing materials or publicity.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Order
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means either of the following that incorporates or
                    references these Service Terms and has been accepted by
                    Admin Tools: (i) an order submitted by Customer on the Site
                    using Admin Tools online ordering process that references a
                    service plan published on the Site; or (ii) an order form
                    prepared by Admin Tools for Customer’s signature that that
                    describes the features of the Admin Tools Services, the
                    related fees, initial term and other transactions details,
                    either in the Order or by reference to a plan description
                    published on the Site. If there are multiple Orders for
                    Services covering a Web Application those Orders are
                    considered a single “Order” as that term is used in these
                    Service Terms. Acceptance by Admin Tools may be made by any
                    reasonable means including processing of Customer’s payment,
                    activation of the Services, or signature on the submitted
                    order. For clarity a transaction document that includes the
                    information described in this definition is an “Order” for
                    purposes of this Agreement even if styled “Statement of
                    Work,” “Order Form,” “Proposal,” or with other names.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Personal Data
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means any information about a natural person that is
                    identified or identifiable to the natural person, either
                    alone or in combination with other information, that Admin
                    Tools will process or have access to as part of providing
                    the Services, including any such information that is created
                    by means of the Services.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Malware
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means any virus, malware, spyware, ransomware, adware, or
                    other code, circuitry or other technological means that is
                    designed to interrupt the normal use of the Services or the
                    systems on which any part of the Services are installed or
                    with which the interoperate, destroy or corrupt any data,
                    make data unavailable, or covertly transmit data or
                    information.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Services
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means Admin Tools monitoring and other services described in
                    an Order and Support.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Site
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means https://www.admintools.io , or any other website
                    operated by Admin Tools for the purpose of offering or
                    providing Services, including any subdomains.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Support
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means the use of the Documentation, and any technical
                    support.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Term
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means the initial term and any renewal terms, collectively.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Admin Tools Technology
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'body1'}>
                    means (i) the Services, mobile applications, user and
                    programming interfaces and all other information, materials,
                    and technology provided by Admin Tools for Customer’s use as
                    part of or in connection with the Services, or used by Admin
                    Tools to provide the Services, (ii) all documentation
                    related to any of the foregoing, (iii) all modifications and
                    derivative works to or of any of the foregoing, and (iv) all
                    related rights in intellectual property, worldwide.
                  </Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', p: { xs: 0.1 } }}>
                  <Typography sx={{ mt: { xs: 0.2, lg: 3 } }} variant={'h4'}>
                    Web Application
                  </Typography>
                  <Typography sx={{ mt: { xs: 0.1, lg: 3 } }} variant={'body1'}>
                    means Customer’s website or other Web-accessible application
                    described in an Order.
                  </Typography>
                </ListItem>
              </List>
            </ListItem>
          </List>
        </Box>
        <BackButton />
      </Container>
    </>
  );
}

export default TermsContent;
